import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, RotateCcw, Save, ShieldCheck, Sliders, Undo2, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mn = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yy}, ${hh}:${mn}`;
  } catch { return iso; }
}

function fmtPct(n) {
  if (n == null || n === "") return "—";
  return `${Number(n).toFixed(1)}%`;
}

export function KpiOverrideDialog({ open, onOpenChange, group, parks, month, monthLabel }) {
  const groupParks = useMemo(() => {
    const ids = new Set(group?.park_ids || []);
    return (parks || []).filter((p) => ids.has(p.id));
  }, [group, parks]);

  const [parkId, setParkId] = useState("");
  useEffect(() => {
    if (open) setParkId(groupParks[0]?.id || "");
  }, [open, groupParks]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="kpi-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sliders className="h-5 w-5 text-amber-700" /> Manual KPI Overrides</DialogTitle>
          <DialogDescription>
            <span className="block">Group: <span className="font-medium text-foreground">{group?.name}</span> · Month: <span className="font-medium text-foreground">{monthLabel}</span></span>
            <span className="mt-1 block text-xs">
              Overrides are saved as <em>draft</em>; the DOCX continues to use auto-computed values until you click
              <span className="mx-1 inline-flex items-center gap-1 rounded border bg-emerald-50 px-1.5 text-[10px] font-semibold uppercase text-emerald-700"><Lock className="h-3 w-3" /> Final Accept</span>.
            </span>
          </DialogDescription>
        </DialogHeader>
        {/* Park picker */}
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <Label className="text-xs">Park</Label>
            <Select value={parkId} onValueChange={setParkId} disabled={groupParks.length === 0}>
              <SelectTrigger data-testid="kpi-override-park-select"><SelectValue placeholder={groupParks.length === 0 ? "No parks in this group" : "Pick a park"} /></SelectTrigger>
              <SelectContent>
                {groupParks.map((p) => (<SelectItem key={p.id} value={p.id} data-testid={`kpi-override-park-option-${p.id}`}><span className="font-mono mr-2 text-slate-500">{p.code}</span>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Editor */}
        {parkId ? (<KpiEditor parkId={parkId} month={month} key={`${parkId}-${month}`} />) : (
          <Alert><AlertTitle>Select a park</AlertTitle><AlertDescription>Pick a park above to edit KPI overrides for that park.</AlertDescription></Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiEditor({ parkId, month }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});  // { kpi_no: "string-or-empty" }
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/monthly-reports/kpis`, { params: { park_id: parkId, ym: month } });
      setData(data);
      const d = {};
      for (const r of data.rows) d[r.kpi_no] = r.override_pct == null ? "" : String(r.override_pct);
      setDraft(d);
    } catch (e) {
      console.error(e);
      toast.error("Could not load KPI data");
    } finally { setLoading(false); }
  }, [parkId, month]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (<Skeleton key={i} className="h-14 w-full" />))}
      </div>
    );
  }

  const finalized = data.finalized;
  // Preview math: while drafting, the "Effective %" preview shows what the
  // value WOULD become if the admin clicks Final Accept (so they see the
  // impact before committing). Once finalized, effective = saved override
  // (or auto if no override is set for that KPI).
  const effectiveFor = (r) => {
    const ov = draft[r.kpi_no] === "" ? null : Number(draft[r.kpi_no]);
    const overrideValid = ov != null && Number.isFinite(ov) && ov >= 0 && ov <= 100;
    return overrideValid ? ov : r.auto_pct;
  };
  const overallAuto = data.rows.reduce((s, r) => s + (r.auto_pct * r.weight / 100), 0);
  const overallEffective = data.rows.reduce((s, r) => s + (effectiveFor(r) * r.weight / 100), 0);

  const setVal = (no, v) => setDraft((d) => ({ ...d, [no]: v }));

  const saveDraft = async () => {
    if (finalized) return toast.error("Re-open the month before editing further");
    setSaving(true);
    try {
      const overrides = data.rows.map((r) => {
        const raw = (draft[r.kpi_no] || "").trim();
        if (raw === "") return { kpi_no: r.kpi_no, override_pct: null };
        const n = Number(raw);
        if (!Number.isFinite(n)) return { kpi_no: r.kpi_no, override_pct: null };
        return { kpi_no: r.kpi_no, override_pct: Math.max(0, Math.min(100, n)) };
      });
      await api.put("/admin/monthly-reports/kpis", { park_id: parkId, ym: month, overrides });
      toast.success("Draft saved — DOCX still uses auto values until Final Accept");
      await reload();
    } catch (e) {
      console.error(e); toast.error("Failed to save overrides");
    } finally { setSaving(false); }
  };

  const finalAccept = async () => {
    setAccepting(true);
    try {
      // First save the current draft before finalizing (so latest values stick)
      const overrides = data.rows.map((r) => {
        const raw = (draft[r.kpi_no] || "").trim();
        if (raw === "") return { kpi_no: r.kpi_no, override_pct: null };
        const n = Number(raw);
        if (!Number.isFinite(n)) return { kpi_no: r.kpi_no, override_pct: null };
        return { kpi_no: r.kpi_no, override_pct: Math.max(0, Math.min(100, n)) };
      });
      await api.put("/admin/monthly-reports/kpis", { park_id: parkId, ym: month, overrides });
      await api.post("/admin/monthly-reports/kpis/finalize", { park_id: parkId, ym: month });
      toast.success("Final accepted — DOCX will now use these KPI values");
      await reload();
    } catch (e) { console.error(e); toast.error("Could not finalize"); }
    finally { setAccepting(false); }
  };

  const reopen = async () => {
    setAccepting(true);
    try {
      await api.post("/admin/monthly-reports/kpis/reopen", { park_id: parkId, ym: month });
      toast.success("Re-opened for editing — DOCX is back to auto values");
      await reload();
    } catch (e) { console.error(e); toast.error("Could not re-open"); }
    finally { setAccepting(false); }
  };

  const clearAll = () => {
    const d = {};
    for (const r of data.rows) d[r.kpi_no] = "";
    setDraft(d);
  };

  return (
    <div className="space-y-3" data-testid="kpi-editor">
      {finalized ? (
        <Alert className="border-emerald-200 bg-emerald-50/60">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          <AlertTitle className="text-emerald-900">Finalized — DOCX uses these values</AlertTitle>
          <AlertDescription className="text-emerald-900/90 text-[12px]">
            Finalized by <b>{data.finalized_by || "—"}</b> on <b>{fmtDateTime(data.finalized_at)}</b>. Use <em>Re-open</em> to edit.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-amber-200 bg-amber-50/60">
          <Unlock className="h-4 w-4 text-amber-800" />
          <AlertTitle className="text-amber-900">Draft mode</AlertTitle>
          <AlertDescription className="text-amber-900/90 text-[12px]">
            Edits below are saved as draft. The downloaded DOCX uses <b>auto-computed</b> values until you click <b>Final Accept</b>.
          </AlertDescription>
        </Alert>
      )}

      {/* Header row */}
      <div className="hidden grid-cols-[42px_minmax(0,1fr)_70px_92px_92px_92px] items-center gap-2 px-2 text-[11px] uppercase tracking-wider text-slate-500 md:grid">
        <div>KPI</div>
        <div>Name</div>
        <div className="text-right">Weight</div>
        <div className="text-right">Auto %</div>
        <div className="text-right">Override %</div>
        <div className="text-right">Effective %</div>
      </div>

      <div className="space-y-1.5">
        {data.rows.map((r) => {
          const raw = draft[r.kpi_no];
          const ov = raw === "" ? null : Number(raw);
          const overrideValid = ov != null && Number.isFinite(ov) && ov >= 0 && ov <= 100;
          const effective = effectiveFor(r);
          const differs = overrideValid && Math.abs(ov - r.auto_pct) >= 0.05;
          return (
            <div key={r.kpi_no} className={`grid grid-cols-2 items-center gap-2 rounded-lg border bg-white px-3 py-2 md:grid-cols-[42px_minmax(0,1fr)_70px_92px_92px_92px]`} data-testid={`kpi-row-${r.kpi_no.replace(/\s+/g, "-")}`}>
              <div className="font-mono text-xs text-slate-500">{r.kpi_no}</div>
              <div className="text-sm font-medium text-slate-800 line-clamp-1">{r.name}</div>
              <div className="text-right text-xs text-slate-500"><Badge variant="outline" className="font-mono">{r.weight}</Badge></div>
              <div className="text-right text-sm tabular-nums text-slate-700" data-testid={`kpi-auto-${r.kpi_no.replace(/\s+/g, "-")}`}>{fmtPct(r.auto_pct)}</div>
              <div>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={raw}
                  onChange={(e) => setVal(r.kpi_no, e.target.value)}
                  disabled={finalized}
                  placeholder="auto"
                  className={`h-8 w-[88px] tabular-nums text-right ${overrideValid && differs ? "border-amber-400" : ""}`}
                  data-testid={`kpi-override-input-${r.kpi_no.replace(/\s+/g, "-")}`}
                />
              </div>
              <div className={`text-right text-sm font-semibold tabular-nums ${differs ? (finalized ? "text-emerald-700" : "text-amber-700") : "text-slate-700"}`} data-testid={`kpi-effective-${r.kpi_no.replace(/\s+/g, "-")}`}>{fmtPct(effective)}</div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between rounded-lg border bg-slate-50 px-3 py-2">
        <div className="text-xs text-slate-500">Weighted Score</div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-500">Auto: <span className="font-mono font-semibold text-slate-800" data-testid="kpi-overall-auto">{overallAuto.toFixed(1)}%</span></span>
          <span className={`${finalized ? "text-emerald-800" : "text-amber-800"} font-mono font-semibold`} data-testid="kpi-overall-effective">{finalized ? "Effective" : "If accepted"}: {overallEffective.toFixed(1)}%</span>
        </div>
      </div>

      <DialogFooter className="!justify-between gap-2 sm:!justify-between">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" type="button" onClick={clearAll} disabled={finalized || saving || accepting} data-testid="kpi-clear-button"><Undo2 className="mr-1 h-4 w-4" /> Clear all</Button>
          <Button variant="outline" type="button" onClick={reload} disabled={saving || accepting} data-testid="kpi-reload-button"><RotateCcw className="mr-1 h-4 w-4" /> Reload</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {!finalized ? (
            <>
              <Button variant="secondary" type="button" onClick={saveDraft} disabled={saving || accepting} data-testid="kpi-save-draft-button"><Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save draft"}</Button>
              <Button type="button" onClick={finalAccept} disabled={saving || accepting} className="bg-emerald-700 hover:bg-emerald-800" data-testid="kpi-final-accept-button"><CheckCircle2 className="mr-1 h-4 w-4" /> {accepting ? "Accepting…" : "Final Accept"}</Button>
            </>
          ) : (
            <Button variant="outline" type="button" onClick={reopen} disabled={accepting} data-testid="kpi-reopen-button"><Unlock className="mr-1 h-4 w-4" /> {accepting ? "Re-opening…" : "Re-open for edits"}</Button>
          )}
        </div>
      </DialogFooter>
    </div>
  );
}
